// src/chatGPT/hooks/useChatHistory.js
import { useState, useCallback } from 'react';
import { db, projectAuth } from '../../firebase/config';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

export function useChatHistory() {
  const [loadingChat, setLoadingChat] = useState(false);
  const [savingChat, setSavingChat] = useState(false);
  const user = projectAuth.currentUser;

  // Save current chat to Firestore
  const saveChat = useCallback(async (chatId, messages, title = null) => {
    if (!user?.uid || messages.length === 0) return false;

    // Saving chat to Firebase
    setSavingChat(true);
    try {
      // Generate title from first user message if not provided
      const chatTitle = title || messages.find(m => m.role === 'user')?.text?.slice(0, 50) || 'New Chat';
      
      // Save chat metadata
      const chatRef = doc(db, 'users', user.uid, 'chats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      const chatData = {
        title: chatTitle,
        preview: messages[messages.length - 1]?.text?.slice(0, 100) || '',
        messageCount: messages.length,
        updatedAt: serverTimestamp()
      };
      
      // Only set createdAt if this is a new chat
      if (!chatDoc.exists()) {
        chatData.createdAt = serverTimestamp();
      }
      
      await setDoc(chatRef, chatData, { merge: true });

      // Save messages to subcollection
      const messagesRef = collection(db, 'users', user.uid, 'chats', chatId, 'messages');
      
      // Save each message
      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const msgRef = doc(messagesRef, `msg_${i}`);
        await setDoc(msgRef, {
          ...msg,
          order: i,
          timestamp: Timestamp.now()
        });
      }
      
      // Chat saved successfully
      return true;
    } catch (error) {
      // Error saving chat
      return false;
    } finally {
      setSavingChat(false);
    }
  }, [user?.uid]);

  // Load a chat from Firestore
  const loadChat = useCallback(async (chatId) => {
    if (!user?.uid) return null;

    setLoadingChat(true);
    try {
      // Get chat metadata
      const chatRef = doc(db, 'users', user.uid, 'chats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        // Chat not found
        return null;
      }

      // Load messages from subcollection
      const messagesRef = collection(db, 'users', user.uid, 'chats', chatId, 'messages');
      const q = query(messagesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        // Remove Firebase-specific fields but keep the document ID
        const { order, timestamp, ...message } = data;
        return {
          id: doc.id, // Include Firestore document ID for React keys
          ...message
        };
      });

      // Store threadId for LangGraph continuity
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(`thread_${chatId}`, chatId);
      }

      return {
        metadata: chatDoc.data(),
        messages
      };
    } catch (error) {
      // Error loading chat
      return null;
    } finally {
      setLoadingChat(false);
    }
  }, [user?.uid]);

  // Generate a chat title from messages
  const generateChatTitle = useCallback(async (messages) => {
    if (messages.length === 0) return 'New Chat';

    // Use first user message as title
    const firstUserMessage = messages.find(m => m.role === 'user');
    if (firstUserMessage) {
      return firstUserMessage.text.slice(0, 50) + (firstUserMessage.text.length > 50 ? '...' : '');
    }

    return 'New Chat';
  }, []);

  // Delete a chat and its messages
  const deleteChat = useCallback(async (chatId) => {
    if (!user?.uid) return false;

    try {
      // Delete all messages in subcollection first
      const messagesRef = collection(db, 'users', user.uid, 'chats', chatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      const deletePromises = messagesSnapshot.docs.map(msgDoc => deleteDoc(msgDoc.ref));
      await Promise.all(deletePromises);

      // Delete the chat document
      await deleteDoc(doc(db, 'users', user.uid, 'chats', chatId));

      return true;
    } catch (error) {
      console.error('Error deleting chat:', error);
      return false;
    }
  }, [user?.uid]);

  // Toggle starred status
  const toggleStarred = useCallback(async (chatId, currentStarred) => {
    if (!user?.uid) return false;

    try {
      const chatRef = doc(db, 'users', user.uid, 'chats', chatId);
      await updateDoc(chatRef, {
        starred: !currentStarred,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error toggling starred:', error);
      return false;
    }
  }, [user?.uid]);

  // Rename a chat
  const renameChat = useCallback(async (chatId, newTitle) => {
    if (!user?.uid || !newTitle.trim()) return false;

    try {
      const chatRef = doc(db, 'users', user.uid, 'chats', chatId);
      await updateDoc(chatRef, {
        title: newTitle.trim(),
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error renaming chat:', error);
      return false;
    }
  }, [user?.uid]);

  return {
    saveChat,
    loadChat,
    generateChatTitle,
    deleteChat,
    toggleStarred,
    renameChat,
    loadingChat,
    savingChat
  };
}